import React, { useContext, useRef, useState } from "react";
import {
  Button,
  Text,
  Avatar,
  VStack,
  HStack,
  Box,
  Badge,
  useDisclosure,
  useToast,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
} from "@chakra-ui/react";
import { UserProfileContext } from "../../context/UserContext";
import { hasUserType, USER_TYPES } from "../../utils/userRole";
import ReusableModal from "../ReusableModal";
import { useLocation, useNavigate } from "react-router-dom";
import EstateList from "./EstateList";
import { BsArrowDown } from "react-icons/bs";
import { BiChevronDown } from "react-icons/bi";

const AccountSwitcher = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { userProfile, activeAccount, setActiveAccount } =
    useContext(UserProfileContext);
  const navigate = useNavigate();
  const location = useLocation();

  const estates = Array.isArray(userProfile?.estates)
    ? userProfile.estates
    : [];
  const isVtu = hasUserType(userProfile, USER_TYPES.VTU_USER);
  const toast = useToast();

  // In-progress water purchase — switching context here silently discards
  // the order the resident is mid-way through, so this case gets a real
  // confirmation step instead of the ordinary switch-and-toast.
  const isMidPurchase = location.pathname.startsWith("/buy-water");
  const { isOpen: isConfirmOpen, onOpen: onConfirmOpen, onClose: onConfirmClose } =
    useDisclosure();
  const pendingSwitchRef = useRef(null);
  const cancelRef = useRef(null);

  const isEstateContext = !!activeAccount?.estateId;
  const contextLabel = isEstateContext ? activeAccount.name : "General Account";
  const contextSubLabel = isEstateContext ? "Estate Account" : null;

  const applyEstateSwitch = (e) => {
    const estate = e?.estateId || e;
    const id = estate?._id || estate;
    const name = estate?.estateName || estate?.name || String(id);
    const isPrimary = !!e?.isPrimary;

    setActiveAccount({ type: "estate", estateId: id, name, isPrimary });
    toast({
      title: `Switched to ${name}`,
      status: "success",
      duration: 2500,
      isClosable: true,
    });
    onClose();
    navigate("/dashboard");
  };

  const applyVtuSwitch = () => {
    setActiveAccount({ type: "vtu", name: "VTU Account" });
    toast({
      title: `Switched to VTU account`,
      status: "success",
      duration: 2500,
      isClosable: true,
    });
    onClose();
    navigate("/vtu");
  };

  const handleSelectEstate = (e) => {
    if (isMidPurchase) {
      pendingSwitchRef.current = () => applyEstateSwitch(e);
      onConfirmOpen();
      return;
    }
    applyEstateSwitch(e);
  };

  const handleSelectVtu = () => {
    if (isMidPurchase) {
      pendingSwitchRef.current = () => applyVtuSwitch();
      onConfirmOpen();
      return;
    }
    applyVtuSwitch();
  };

  const confirmSwitch = () => {
    const run = pendingSwitchRef.current;
    pendingSwitchRef.current = null;
    onConfirmClose();
    run?.();
  };

  return (
    <>
      <Button
        minW={"fit-content"}
        w="100%"
        px={"1em"}
        variant="solid"
        mr="8px"
        onClick={onOpen}
      >
        {/* full-width button: left = dot+text, right = arrow */}
        <HStack w="100%" justifyContent="space-between" alignItems="center">
          <HStack spacing={3} alignItems="center">
            <Box
              bg={activeAccount ? "green.400" : "gray.400"}
              borderRadius="full"
              w="8px"
              h="8px"
            />
            <VStack spacing={0} alignItems="flex-start">
              <Text fontSize="14px" fontWeight="medium">
                {contextLabel}
              </Text>
              {contextSubLabel && (
                <Text fontSize="11px" color="gray.500">
                  {contextSubLabel}
                </Text>
              )}
            </VStack>
          </HStack>
          <BiChevronDown />
        </HStack>
      </Button>

      <ReusableModal isOpen={isOpen} onClose={onClose} title="Switch Account">
        <Box h={"300px"} p={2}>
          <EstateList
            estates={estates}
            onSelectEstate={handleSelectEstate}
            isVtu={isVtu}
            onSelectVtu={handleSelectVtu}
            buttonLabel="Switch"
            activeAccount={activeAccount}
          />
        </Box>
      </ReusableModal>

      <AlertDialog
        isOpen={isConfirmOpen}
        leastDestructiveRef={cancelRef}
        onClose={onConfirmClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Switch account?
            </AlertDialogHeader>
            <AlertDialogBody>
              You have a water purchase in progress. Switching accounts now
              will discard it — you'll need to start over.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onConfirmClose}>
                Stay here
              </Button>
              <Button colorScheme="red" onClick={confirmSwitch} ml={3}>
                Switch anyway
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};

export default AccountSwitcher;
